<template>
  <div class="mag-viewer">
    <!-- 顶部工具栏 -->
    <div class="mag-toolbar">
      <!-- 左侧：显示年份和期数 -->
      <div class="mag-toolbar-left">
        <span class="mag-issue-info">{{ loading ? '加载中...' : issueDisplayText }}</span>
      </div>
      <!-- 中间：上一页/页码/下一页按钮 -->
      <div class="mag-toolbar-center">
        <button class="mag-btn" :disabled="loading || currentPage <= 0" @click="flipPrev">上一页</button>
        <span class="mag-page-display">{{ loading ? '加载中...' : currentPageDisplay }}</span>
        <button class="mag-btn" :disabled="loading || currentPage >= totalPages - 1" @click="flipNext">下一页</button>
      </div>
      <!-- 右侧：购买杂志按钮 -->
      <div class="mag-toolbar-right">
        <button v-show="!loading" class="mag-btn mag-btn-buy" @click="buyMagazine">购买杂志</button>
      </div>
    </div>

    <!-- 翻书舞台 -->
    <div class="mag-stage" ref="bookStage">
      <!-- 加载浮层 -->
      <div v-show="loading" class="mag-loading">
        <div class="mag-loading-spinner"></div>
        <span>正在加载杂志...</span>
      </div>

      <!-- page-flip 容器 -->
      <div ref="flipbook" class="mag-flipbook"></div>

      <!-- 左右翻页箭头 -->
      <div
        v-show="!loading"
        class="mag-nav-arrow mag-nav-left"
        :class="{ disabled: currentPage <= 0 }"
        @click="flipPrev"
      >
        <svg viewBox="0 0 24 24" width="36" height="36">
          <path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div
        v-show="!loading"
        class="mag-nav-arrow mag-nav-right"
        :class="{ disabled: currentPage >= totalPages - 1 }"
        @click="flipNext"
      >
        <svg viewBox="0 0 24 24" width="36" height="36">
          <path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
    </div>

    <!-- 购买弹窗 -->
    <div v-if="showBuyDialog" class="mag-buy-dialog-overlay" @click.self="closeBuyDialog">
      <!-- 选择页面 -->
      <transition name="mag-dialog-fade">
        <div v-if="buyStep === 1" class="mag-buy-dialog mag-buy-dialog-select">
          <div class="mag-dialog-header">
            <h3>{{ buyProductTitle }}</h3>
          </div>

          <div class="mag-dialog-content">
            <!-- 选择类型 -->
            <div class="mag-select-section">
              <label class="mag-label">请选择您购买的类型：</label>
              <div class="mag-radio-group">
                <label class="mag-radio-item">
                  <input type="radio" value="本期" v-model="productType" @change="handleProductTypeChange">
                  <span class="mag-radio-text">本期</span>
                </label>
                <label class="mag-radio-item">
                  <input type="radio" value="年刊" v-model="productType" @change="handleProductTypeChange">
                  <span class="mag-radio-text">年刊</span>
                </label>
              </div>
            </div>

            <!-- 杂志类型 -->
            <div class="mag-select-section">
              <label class="mag-label">杂志类型：</label>
              <div class="mag-radio-group">
                <label class="mag-radio-item">
                  <input type="radio" value="physical" v-model="purchaseItemType" @change="calculateTotalPrice">
                  <span class="mag-radio-text">纸质版本</span>
                </label>
              </div>
            </div>

            <!-- 单本金额 -->
            <div class="mag-info-row">
              <span class="mag-info-label">单本金额：</span>
              <span class="mag-info-value">¥{{ unitPrice.toFixed(2) }}</span>
            </div>

            <!-- 购买数量 -->
            <div class="mag-quantity-section">
              <label class="mag-label">购买数量：</label>
              <div class="mag-quantity-control">
                <button class="mag-qty-btn" @click="handleQuantityChange(-1)" :disabled="quantity <= 1">-</button>
                <span class="mag-qty-value">{{ quantity }}</span>
                <button class="mag-qty-btn" @click="handleQuantityChange(1)" :disabled="quantity >= 99">+</button>
              </div>
            </div>

            <!-- 支付金额 -->
            <div class="mag-total-section">
              <label class="mag-total-label">支付金额：</label>
              <span class="mag-total-value">¥{{ formattedTotalPrice }}</span>
            </div>

            <!-- 收货人信息 -->
            <div class="mag-address-section">
              <!-- 收货人姓名 -->
              <div class="mag-input-row">
                <label class="mag-label">收货人姓名：</label>
                <input
                  type="text"
                  v-model="receiverName"
                  placeholder="请输入收货人姓名"
                  maxlength="20"
                  class="mag-input-text"
                />
              </div>

              <!-- 手机号码 -->
              <div class="mag-input-row">
                <label class="mag-label">手机号码：</label>
                <input
                  type="tel"
                  v-model="receiverPhone"
                  placeholder="请输入手机号码"
                  maxlength="11"
                  class="mag-input-text"
                  @input="validatePhone"
                />
              </div>

              <!-- 所在地区 -->
              <div class="mag-district-section">
                <label class="mag-label">所在地区：</label>
                <div class="mag-district-row">
                  <select
                    v-model="receiverProvince"
                    class="mag-district-select"
                    @change="onProvinceChange"
                  >
                    <option value="">选择省份</option>
                    <option
                      v-for="province in provinceList"
                      :key="province.value"
                      :value="province.value"
                    >
                      {{ province.label }}
                    </option>
                  </select>

                  <select
                    v-model="receiverCity"
                    class="mag-district-select"
                    :disabled="!receiverProvince"
                    @change="onCityChange"
                  >
                    <option value="">选择城市</option>
                    <option
                      v-for="city in cityList"
                      :key="city.value"
                      :value="city.value"
                    >
                      {{ city.label }}
                    </option>
                  </select>

                  <select
                    v-model="receiverDistrict"
                    class="mag-district-select"
                    :disabled="!receiverCity"
                  >
                    <option value="">选择区县</option>
                    <option
                      v-for="district in districtList"
                      :key="district.value"
                      :value="district.value"
                    >
                      {{ district.label }}
                    </option>
                  </select>
                </div>

                <!-- 限制配送地区提示 -->
                <div class="mag-warning-text" v-if="isRestrictedRegion">
                  新疆、西藏地区暂不支持配送
                </div>
              </div>

              <!-- 详细地址 -->
              <div class="mag-input-row mag-input-row-textarea">
                <label class="mag-label">详细地址：</label>
                <textarea
                  v-model="receiverAddress"
                  placeholder="请输入详细地址"
                  maxlength="100"
                  rows="3"
                  class="mag-input-textarea"
                ></textarea>
              </div>
            </div>

            <!-- 立即支付按钮 -->
            <button
              class="mag-pay-btn"
              :disabled="payLoading || isRestrictedRegion"
              @click="handlePayClick"
            >
              {{ payLoading ? '正在创建订单...' : '立刻支付' }}
            </button>
          </div>
        </div>
      </transition>

      <!-- 支付页面 -->
      <transition name="mag-dialog-fade">
        <div v-if="buyStep === 2" class="mag-buy-dialog mag-buy-dialog-pay">
          <div class="mag-dialog-header">
            <h3>{{ buyProductTitle }}</h3>
          </div>

          <div class="mag-dialog-content">
            <!-- 商品信息摘要 -->
            <div class="mag-order-summary">
              <p>{{ paymentSummary }}</p>
            </div>

            <!-- 二维码 -->
            <div class="mag-qrcode-container">
              <div class="mag-qrcode-box" ref="qrcodeContainer">
                <div v-if="!orderNo" class="mag-qrcode-placeholder">
                  <span>正在生成二维码...</span>
                </div>
              </div>
                <div class="mag-pay-tip">请用微信扫码支付</div>
              <div class="mag-pay-tip" v-if="orderPayTipContent" v-html="orderPayTipContent"></div>
              <!-- 总金额 -->
              <div class="mag-pay-amount">¥{{ formattedTotalPrice }}</div>
            </div>

            <!-- 返回按钮 -->
            <button class="mag-back-btn" @click="buyStep = 1">返回</button>
          </div>
        </div>
      </transition>

      <!-- 支付成功页面 -->
      <transition name="mag-dialog-fade">
        <div v-if="buyStep === 3" class="mag-buy-dialog mag-buy-dialog-success">
          <div class="mag-dialog-content">
            <!-- 成功图标 -->
            <div class="mag-success-icon">
              <svg viewBox="0 0 24 24" width="72" height="72">
                <circle cx="12" cy="12" r="11" fill="none" stroke="#52c41a" stroke-width="2"/>
                <path d="M7 12l3 3 7-7" fill="none" stroke="#52c41a" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>

            <!-- 成功文字 -->
            <h3 class="mag-success-title">支付成功</h3>

            <!-- 提示文字 -->
            <p class="mag-success-desc">
              您已经支付成功，可以去
              <span class="mag-success-link" @click="goToMyOrders">个人中心</span>
              查看电子杂志订单
            </p>
          </div>
        </div>
      </transition>
    </div>

    <!-- 底部缩略导航 -->
    <div v-show="!loading && allImages.length > 0" class="mag-footer">
      <div class="mag-thumb-strip">
        <div
          v-for="(url, idx) in allImages"
          :key="idx"
          class="mag-thumb"
          :class="{ active: isPageVisible(idx), 'mag-thumb-loading': !url }"
          @click="url ? jumpToPage(idx) : null"
        >
          <img v-if="url" :src="url" :alt="'第' + idx + '页'" loading="lazy" />
          <div v-else class="mag-thumb-placeholder"></div>
          <span class="mag-thumb-num">{{ idx === 0 ? '封面' : idx }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { PageFlip } from 'page-flip'
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf'
import { fetchMagazineView } from '../../api'
import { request } from '../../api/request'
// 引入完整省市区数据
import { regionData } from 'element-china-area-data'

pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js'

export default {
  name: 'MagazineViewer',
  data() {
    return {
      loading: true,
      pageFlip: null,
      pageUrls: [],
      totalPages: 0,
      currentPage: 0,
      isMounted: true,
      mouseDownHandler: null,
      mouseUpHandler: null,
      resizeTimer: null,
      magazineName: '',
      year: 0,
      issueNumber: 0,
      isPurchased: false,
      name: '',

      // 购买弹窗相关
      showBuyDialog: false,
      buyStep: 1,
      productType: '本期',
      purchaseItemType: 'physical',
      unitPrice: 0,
      emagazineCode: '',
      payLoading: false,
      orderNo: '',
      singleDigitalPrice: 0,
      singlePrintPrice: 0,
      subscriptionDigitalPrice: 0,
      subscriptionPrintPrice: 0,
      quantity: 1,
      totalPrice: 0,

      payTitle: '',
      QRCodeUrl: '',
      timer: null,

      // ===== 收货人信息 =====
      receiverName: '',
      receiverPhone: '',
      receiverAddress: '',
      receiverProvince: '',
      receiverCity: '',
      receiverDistrict: '',

      // ===== 省市区数据（完整） =====
      regionData: regionData,
      provinceList: [],
      cityList: [],
      districtList: [],

      restrictedRegions: ['54', '65'], // 西藏、新疆
      orderPayTipContent: '',
    }
  },
  computed: {
    allImages() {
      return this.pageUrls
    },
    issueDisplayText() {
      return this.name
    },
    currentPageDisplay() {
      const pageNum = this.currentPage + 1
      return `第 ${pageNum}页 / 共${this.totalPages}页`
    },
    buyProductTitle() {
      if (this.year && this.issueNumber) {
        const issue = this.issueNumber < 10 ? `0${this.issueNumber}` : this.issueNumber
        return `${this.year}年第${issue}期`
      }
      return this.magazineName || '杂志购买'
    },
    
    paymentSummary() {
      let text = ''
      if (this.productType === '本期') {
        text += `本期 * ${this.quantity} 本`
      } else {
        text += `年刊 * ${this.quantity}`
      }
      if (this.purchaseItemType === 'physical') {
        text += ' 纸质版本'
      } else {
        text += ' 电子版本'
      }
      return text
    },
    formattedTotalPrice() {
      return this.totalPrice.toFixed(2)
    },
    effectiveUnitPrice() {
      if (this.productType === '本期') {
        return this.purchaseItemType === 'physical' ? this.singlePrintPrice : this.singleDigitalPrice
      } else {
        return this.purchaseItemType === 'physical' ? this.subscriptionPrintPrice : this.subscriptionDigitalPrice
      }
    },
    // 判断是否为限制配送地区
    isRestrictedRegion() {
      return this.restrictedRegions.includes(this.receiverProvince)
    },
    // 名称回填
    provinceName() {
      const p = this.provinceList.find(item => item.value === this.receiverProvince)
      return p ? p.label : ''
    },
    cityName() {
      const c = this.cityList.find(item => item.value === this.receiverCity)
      return c ? c.label : ''
    },
    districtName() {
      const d = this.districtList.find(item => item.value === this.receiverDistrict)
      return d ? d.label : ''
    }
  },
  async mounted() {
    // 初始化省份列表
    this.provinceList = this.regionData.map(p => ({
      value: p.value,
      label: p.label
    }))

    await this.loadPdf()
    await this.showOrderPayTipInfo()
    if (this.isMounted && this.pageUrls.length > 0) {
      this.$nextTick(() => {
        this.initPageFlip()
      })
    } else if (this.pageUrls.length === 0) {
      this.loading = false
    }
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    this.isMounted = false
    window.removeEventListener('resize', this.handleResize)
    if (this.resizeTimer) clearTimeout(this.resizeTimer)
    if (this.timer) clearInterval(this.timer)
    this.cleanupHandlers()
    if (this.pageFlip) {
      try { this.pageFlip.destroy() } catch (e) { /* 忽略 */ }
      this.pageFlip = null
    }
  },

  beforeRouteLeave(to, from, next) {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
    next()
  },
  methods: {
    initBuyInfo() {
      this.buyStep = 1
      this.productType = '本期'
      this.purchaseItemType = 'physical'
      this.quantity = 1
      this.payTitle = ''
      this.QRCodeUrl = ''
      this.orderNo = ''
      this.payLoading = false
      // 清空收货信息
      this.receiverName = ''
      this.receiverPhone = ''
      this.receiverAddress = ''
      this.receiverProvince = ''
      this.receiverCity = ''
      this.receiverDistrict = ''
      this.cityList = []
      this.districtList = []
      this.calculateTotalPrice()
    },

    calculateTotalPrice() {
      this.unitPrice = this.effectiveUnitPrice
      this.totalPrice = this.unitPrice * this.quantity
    },

    handleProductTypeChange() {
      this.calculateTotalPrice()
    },

    handleQuantityChange(delta) {
      const newQty = this.quantity + delta
      if (newQty >= 1 && newQty <= 99) {
        this.quantity = newQty
        this.calculateTotalPrice()
      }
    },
   async showOrderPayTipInfo() {
      try {
         const content = await request.post('/api/index/getOrderPayTip')
        this.orderPayTipContent = content.data || '<p>暂无支付说明</p>'
      } catch (err) {
       
        this.orderPayTipContent = '<p>加载失败，请稍后重试</p>'
      } 
    },
    // ==================== 省市区联动 ====================
    onProvinceChange() {
      this.receiverCity = ''
      this.receiverDistrict = ''
      this.districtList = []

      const province = this.regionData.find(p => p.value === this.receiverProvince)
      if (province && province.children) {
        this.cityList = province.children.map(c => ({
          value: c.value,
          label: c.label
        }))
      } else {
        this.cityList = []
      }
    },

    onCityChange() {
      this.receiverDistrict = ''

      const province = this.regionData.find(p => p.value === this.receiverProvince)
      if (province && province.children) {
        const city = province.children.find(c => c.value === this.receiverCity)
        if (city && city.children) {
          this.districtList = city.children.map(d => ({
            value: d.value,
            label: d.label
          }))
        } else {
          this.districtList = []
        }
      } else {
        this.districtList = []
      }
    },

    validatePhone() {
      this.receiverPhone = this.receiverPhone.replace(/[^0-9]/g, '')
    },

    canPay() {
      if (!this.receiverName.trim()) {
        alert('请输入收货人姓名')
        return false
      }
      if (!this.receiverPhone || this.receiverPhone.length !== 11) {
        alert('请输入正确的手机号码')
        return false
      }
      if (!this.receiverProvince) {
        alert('请选择所在地区')
        return false
      }
      if (!this.receiverAddress.trim()) {
        alert('请输入详细地址')
        return false
      }
      if (this.isRestrictedRegion) {
        alert('新疆、西藏地区暂不支持配送')
        return false
      }
      return true
    },

    async handlePayClick() {
      // 先校验收货信息
      if (!this.canPay()) return

      if (!this.emagazineCode) {
        this.$toast && this.$toast('缺少杂志编号，请刷新重试')
        return
      }
      const periodType = this.productType === '年刊' ? 2 : 1
      const mediaType = this.purchaseItemType === 'physical' ? 2 : 1
      const params = {
        emagazineCode: this.emagazineCode,
        periodType,
        mediaType,
        quantity: this.quantity,
        // 收货人信息
        receiverName: this.receiverName,
        receiverPhone: this.receiverPhone,
        receiverAddress: this.receiverAddress,
        receiverProvince: this.receiverProvince,
        receiverProvinceName: this.provinceName,
        receiverCity: this.receiverCity,
        receiverCityName: this.cityName,
        receiverDistrict: this.receiverDistrict,
        receiverDistrictName: this.districtName
      }
      try {
        this.payLoading = true
        const res = await request({
          url: '/api/order/createOrder',
          method: 'post',
          data: params
        })
        if (res.code === 200 && res.data) {
          this.orderNo = res.data
          const wxRes = await request({
            url: '/api/order/wxUnifiedOrder',
            method: 'post',
            data: { orderNo: this.orderNo, type: 'web' }
          })
          if (wxRes.code === 200 && wxRes.data && wxRes.data.code_url) {
            this.buyStep = 2
            this.$nextTick(() => {
              this.generateQRCode(wxRes.data.code_url)
            })
            this.startPaymentCheck()
          } else {
            this.$toast && this.$toast(wxRes.msg || '获取支付二维码失败，请重试')
          }
        } else {
          this.$toast && this.$toast(res.msg || '创建订单失败，请重试')
        }
      } catch (error) {
        console.error('创建订单失败', error)
        const errorMsg = error.message || error.msg || (typeof error === 'string' ? error : '创建订单失败，请检查网络后重试')
        if (window.alert) {
          window.alert(`❌ 订单创建失败\n\n原因：${errorMsg}`)
        }
      } finally {
        this.payLoading = false
      }
    },

    generateQRCode(url) {
      const container = this.$refs.qrcodeContainer
      if (!container) return
      container.innerHTML = ''
      const QRCode = require('qrcodejs2')
      new QRCode(container, {
        text: url,
        width: 200,
        height: 200,
        colorDark: '#000000',
        colorLight: '#ffffff',
        correctLevel: QRCode.CorrectLevel.H
      })
      this.$nextTick(() => {
        const canvas = container.querySelector('canvas')
        if (!canvas) return
        const ctx = canvas.getContext('2d')
        const logo = new Image()
        logo.crossOrigin = 'anonymous'
        logo.src = 'https://res.wx.qq.com/a/wx_fed/assets/res/NTI4MWU5.ico'
        logo.onload = () => {
          const logoSize = 40
          const x = (canvas.width - logoSize) / 2
          const y = (canvas.height - logoSize) / 2
          ctx.fillStyle = '#ffffff'
          ctx.fillRect(x - 2, y - 2, logoSize + 4, logoSize + 4)
          ctx.drawImage(logo, x, y, logoSize, logoSize)
        }
      })
    },

    startPaymentCheck() {
      if (this.timer) clearInterval(this.timer)
      this.timer = setInterval(async () => {
        try {
          const res = await request({
            url: '/api/order/getPayStatus',
            method: 'post',
            data: { orderNo: this.orderNo }
          })
          if (res.code === 200 && res.data && res.data.payStatus === 1) {
            clearInterval(this.timer)
            this.timer = null
            this.buyStep = 3
          }
        } catch (error) {
          console.error('检查支付状态失败', error)
        }
      }, 1000)
    },

    goToMyOrders() {
      this.closeBuyDialog()
      this.$router.push('/my')
    },

    closeBuyDialog() {
      this.showBuyDialog = false
      this.buyStep = 1
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },

    cleanupHandlers() {
      if (this.mouseDownHandler && this.$refs.flipbook) {
        this.$refs.flipbook.removeEventListener('mousedown', this.mouseDownHandler)
      }
      if (this.mouseUpHandler) {
        window.removeEventListener('mouseup', this.mouseUpHandler)
      }
      this.mouseDownHandler = null
      this.mouseUpHandler = null
    },

    async loadPdf() {
      try {
        const emagazineCode = this.$route.query.emagazineCode
        const codeToken = this.$route.query.codeToken

        if (!emagazineCode) {
          console.error('缺少 emagazineCode 参数')
          this.$router.push('/not-found')
          return
        }

        this.emagazineCode = emagazineCode

        const params = { emagazineCode }
        if (codeToken) {
          params.codeToken = codeToken
        }

        const data = await fetchMagazineView(params)

        if (!data || !data.pdfPath || !data.name) {
          console.error('杂志信息获取失败', data)
          this.$router.push('/not-found')
          return
        }

        this.magazineName = data.name
        this.year = data.year || 0
        this.issueNumber = data.issueNumber || 0
        this.singleDigitalPrice = data.singleDigitalPrice || 0
        this.singlePrintPrice = data.singlePrintPrice || 0
        this.subscriptionDigitalPrice = data.subscriptionDigitalPrice || 0
        this.subscriptionPrintPrice = data.subscriptionPrintPrice || 0
        this.name = data.name || ''

        if (data.hasPurchase) {
          this.isPurchased = true
        } else if (Array.isArray(data.purchasedUserIds)) {
          const userId = localStorage.getItem('userId') || localStorage.getItem('memberId')
          this.isPurchased = data.purchasedUserIds.includes(userId)
        }

        const pdfPath = data.pdfPath
        if (!pdfPath) {
          console.error('接口未返回 pdfPath')
          this.$router.push('/not-found')
          return
        }

        const loadingTask = pdfjsLib.getDocument(pdfPath)
        this.pdfDoc = await loadingTask.promise
        const numPages = this.pdfDoc.numPages

        const pageUrls = new Array(numPages).fill(null)
        pageUrls[0] = await this.renderPage(1)
        this.pageUrls = pageUrls

        this.loadNextChunk(2, numPages, 5)
      } catch (err) {
        console.error('PDF 加载失败', err)
        this.$router.push('/not-found')
      }
    },

    async loadNextChunk(startPage, numPages, chunkSize) {
      if (startPage > numPages) return
      const endPage = Math.min(startPage + chunkSize - 1, numPages)
      const pageUrls = [...this.pageUrls]

      for (let i = startPage; i <= endPage; i++) {
        try {
          pageUrls[i - 1] = await this.renderPage(i)
        } catch (e) {
          console.error(`渲染第 ${i} 页失败`, e)
        }
      }
      this.pageUrls = pageUrls

      this.patchPageImages(startPage, endPage)

      this.loadNextChunk(endPage + 1, numPages, chunkSize)
    },

    patchPageImages(startPage, endPage) {
      const container = this.$refs.flipbook
      if (!container) return
      const pages = container.querySelectorAll('.mag-flip-page')
      for (let i = startPage; i <= endPage; i++) {
        const pageEl = pages[i - 1]
        if (!pageEl) continue
        const url = this.pageUrls[i - 1]
        if (!url) continue
        let img = pageEl.querySelector('img')
        if (!img) {
          img = document.createElement('img')
          img.draggable = false
          pageEl.innerHTML = ''
          pageEl.appendChild(img)
        }
        if (img.src !== url) {
          img.src = url
        }
      }
    },

    async renderPage(pageNum) {
      const page = await this.pdfDoc.getPage(pageNum)
      const scale = 1.5
      const viewport = page.getViewport({ scale })
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      canvas.width = viewport.width
      canvas.height = viewport.height
      await page.render({ canvasContext: ctx, viewport }).promise
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85)

      try {
        const img = new Image()
        img.src = dataUrl
        if (img.decode) await img.decode()
      } catch (e) { /* 忽略 */ }

      return dataUrl
    },

    createPageElements() {
      const pages = []
      if (this.pageUrls.length === 0) return pages

      const coverPage = document.createElement('div')
      coverPage.className = 'mag-flip-page mag-cover-page'
      coverPage.setAttribute('data-density', 'hard')
      coverPage.setAttribute('data-page-index', '0')
      if (this.pageUrls[0]) {
        const coverImg = document.createElement('img')
        coverImg.src = this.pageUrls[0]
        coverImg.draggable = false
        coverPage.appendChild(coverImg)
      } else {
        coverPage.appendChild(this.createPagePlaceholder())
      }
      pages.push(coverPage)

      this.pageUrls.slice(1).forEach((url, idx) => {
        const page = document.createElement('div')
        page.className = 'mag-flip-page'
        page.setAttribute('data-page-index', String(idx + 1))
        if (url) {
          const img = document.createElement('img')
          img.src = url
          img.draggable = false
          page.appendChild(img)
        } else {
          page.appendChild(this.createPagePlaceholder())
        }
        pages.push(page)
      })

      const backCover = document.createElement('div')
      backCover.className = 'mag-flip-page mag-flip-page-back mag-back-cover-page'
      backCover.setAttribute('data-density', 'hard')
      backCover.setAttribute('data-page-index', String(this.pageUrls.length))
      pages.push(backCover)

      return pages
    },

    createPagePlaceholder() {
      const div = document.createElement('div')
      div.className = 'mag-page-placeholder'
      div.innerHTML = '<span>加载中...</span>'
      return div
    },

    calcPageSize() {
      const stage = this.$refs.bookStage
      const availW = Math.max(200, stage.clientWidth - 20)
      const availH = Math.max(200, stage.clientHeight - 20)

      const ratio = 795 / 615

      let pageW = availW / 2
      let pageH = pageW * ratio

      if (pageH > availH) {
        pageH = availH
        pageW = pageH / ratio
      }

      return { pageW: Math.floor(pageW), pageH: Math.floor(pageH) }
    },

    initPageFlip() {
      const container = this.$refs.flipbook
      if (!container) return

      this.cleanupHandlers()
      if (this.pageFlip) {
        try { this.pageFlip.destroy() } catch (e) { /* 忽略 */ }
        this.pageFlip = null
      }

      container.innerHTML = ''
      container.className = 'mag-flipbook'

      const pages = this.createPageElements()
      const { pageW, pageH } = this.calcPageSize()

      this.pageFlip = new PageFlip(container, {
        width: pageW,
        height: pageH,
        size: 'fixed',
        autoSize: false,
        showCover: true,
        maxShadowOpacity: 0.5,
        flippingTime: 800,
        drawShadow: true,
        usePortrait: false,
        showPageCorners: false,
        useMouseEvents: false,
        mobileScrollSupport: false,
        swipeDistance: 30,
        clickEventForward: true,
        disableFlipByClick: true
      })

      this.pageFlip.loadFromHTML(pages)
      this.totalPages = this.pageFlip.getPageCount()

      this.pageFlip.on('flip', (e) => {
        this.currentPage = e.data
      })

      let mouseDownOnBook = false
      let mouseDownX = 0
      let mouseDownY = 0

      this.mouseDownHandler = (e) => {
        mouseDownOnBook = true
        mouseDownX = e.clientX
        mouseDownY = e.clientY
      }

      this.mouseUpHandler = (e) => {
        if (!mouseDownOnBook) return
        mouseDownOnBook = false
        const dx = Math.abs(e.clientX - mouseDownX)
        const dy = Math.abs(e.clientY - mouseDownY)
        if (dx < 5 && dy < 5) {
          if (this.currentPage === 0) {
            this.flipNext()
          } else if (this.currentPage === this.totalPages - 1) {
            this.flipPrev()
          } else {
            const rect = container.getBoundingClientRect()
            const clickX = e.clientX - rect.left
            if (clickX > rect.width / 2) {
              this.flipNext()
            } else {
              this.flipPrev()
            }
          }
        }
      }

      container.addEventListener('mousedown', this.mouseDownHandler)
      window.addEventListener('mouseup', this.mouseUpHandler)

      this.$nextTick(() => {
        requestAnimationFrame(() => {
          if (this.pageFlip) {
            try { this.pageFlip.update() } catch (e) { /* 忽略 */ }
          }
          this.loading = false

          const turnPage = this.$route.query.turnPage
          if (turnPage) {
            const pageIndex = parseInt(turnPage, 10) - 1
            if (!isNaN(pageIndex) && pageIndex >= 0 && pageIndex < this.totalPages) {
              this.$nextTick(() => {
                this.jumpToPage(pageIndex)
              })
            }
          }
        })
      })
    },

    handleResize() {
      if (this.resizeTimer) clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        if (this.loading || !this.pageFlip) return
        const { pageW, pageH } = this.calcPageSize()
        this.pageFlip.setting.width = pageW
        this.pageFlip.setting.height = pageH
        this.pageFlip.update()
      }, 300)
    },

    withFlipClickEnabled(action) {
      if (!this.pageFlip) return
      const original = this.pageFlip.setting.disableFlipByClick
      this.pageFlip.setting.disableFlipByClick = false
      try {
        action()
      } finally {
        this.pageFlip.setting.disableFlipByClick = original
      }
    },

    flipNext() {
      if (!this.pageFlip || this.loading) return
      const before = this.currentPage
      try {
        this.withFlipClickEnabled(() => this.pageFlip.flipNext())
        setTimeout(() => {
          if (this.currentPage === before) {
            try { this.pageFlip.turnToNextPage() } catch (e) { console.error('无动画翻页失败', e) }
          }
        }, 1000)
      } catch (e) {
        console.error('flipNext 失败', e)
        try { this.pageFlip.turnToNextPage() } catch (e2) { /* 忽略 */ }
      }
    },

    flipPrev() {
      if (!this.pageFlip || this.loading) return
      const before = this.currentPage
      try {
        this.withFlipClickEnabled(() => this.pageFlip.flipPrev())
        setTimeout(() => {
          if (this.currentPage === before) {
            try { this.pageFlip.turnToPrevPage() } catch (e) { console.error('无动画翻页失败', e) }
          }
        }, 1000)
      } catch (e) {
        console.error('flipPrev 失败', e)
        try { this.pageFlip.turnToPrevPage() } catch (e2) { /* 忽略 */ }
      }
    },

    jumpToPage(pageNum) {
      if (!this.pageFlip || this.loading) return
      const before = this.currentPage
      try {
        this.withFlipClickEnabled(() => this.pageFlip.flip(pageNum))
        setTimeout(() => {
          if (this.currentPage === before) {
            try { this.pageFlip.turnToPage(pageNum) } catch (e) { console.error('直接跳转失败', e) }
          }
        }, 1000)
      } catch (e) {
        console.error('jumpToPage 失败', e)
        try { this.pageFlip.turnToPage(pageNum) } catch (e2) { /* 忽略 */ }
      }
    },

    async buyMagazine() {
      const token = localStorage.getItem('token')
      if (!token) {
        window.location.href = `/login?redirect=${encodeURIComponent(this.$route.fullPath)}`
        return
      }

      try {
        const res = await request({
          url: '/api/member/home/getUserInfo',
          method: 'post'
        })

        if (res.code === 200 && res.data) {
          this.showBuyDialog = true
          this.initBuyInfo()
        } else {
          window.location.href = `/login?redirect=${encodeURIComponent(this.$route.fullPath)}`
        }
      } catch (error) {
        console.error('获取用户信息失败', error)
        window.location.href = `/login?redirect=${encodeURIComponent(this.$route.fullPath)}`
      }
    },

    goBack() {
      this.$router.back()
    },

    isPageVisible(pageNum) {
      if (this.currentPage === 0) return pageNum === 0
      if (this.currentPage === this.totalPages - 1) return pageNum === this.totalPages - 1
      return pageNum === this.currentPage || pageNum === this.currentPage + 1
    }
  }
}
</script>

<style>
/* ===== 全局容器 ===== */
.mag-viewer {
  height: 100vh;
  min-height: 100vh;
  background: radial-gradient(ellipse at 50% 40%, #1e1e38 0%, #12121f 60%, #0a0a12 100%);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ===== 顶部工具栏 ===== */
.mag-toolbar {
  height: 50px;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  z-index: 100;
}

.mag-toolbar-left {
  flex: 1;
  display: flex;
  align-items: center;
}
.mag-issue-info {
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  font-family: 'Microsoft YaHei', sans-serif;
  letter-spacing: 1px;
  white-space: nowrap;
}

.mag-toolbar-center {
  flex: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.mag-page-display {
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  white-space: nowrap;
  padding: 0 4px;
  min-width: 60px;
  text-align: center;
  font-family: 'Microsoft YaHei', sans-serif;
}

.mag-toolbar-right {
  flex: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.mag-btn {
  padding: 6px 18px;
  background: rgba(160, 0, 0, 0.7);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.25s;
  white-space: nowrap;
}
.mag-btn:hover:not(:disabled) {
  background: rgba(160, 0, 0, 1);
  box-shadow: 0 2px 16px rgba(160, 0, 0, 0.45);
  transform: translateY(-1px);
}
.mag-btn:disabled {
  background: rgba(60, 60, 60, 0.4);
  cursor: not-allowed;
  border-color: transparent;
}
.mag-btn-buy {
  background: linear-gradient(135deg, #A00000, #700000);
  font-weight: bold;
}
.mag-btn-buy:hover:not(:disabled) {
  background: linear-gradient(135deg, #C00000, #900000);
  box-shadow: 0 4px 20px rgba(160, 0, 0, 0.6);
}

/* ===== 翻书舞台 ===== */
.mag-stage {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 10px;
  position: relative;
  min-height: 0;
}

/* ===== 加载浮层 ===== */
.mag-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 16px;
  z-index: 50;
}
.mag-loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: rgba(160, 0, 0, 0.8);
  border-radius: 50%;
  animation: mag-spin 0.8s linear infinite;
}
@keyframes mag-spin {
  to { transform: rotate(360deg); }
}

/* ===== page-flip 容器 ===== */
.mag-flipbook {
  width: 100%;
  height: 100%;
  align-self: stretch;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mag-flip-page {
  background: #fff;
  overflow: hidden;
  width: 100%;
  height: 100%;
}
.mag-flip-page img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  pointer-events: none;
}
.mag-flip-page-back {
  background: linear-gradient(135deg, #8a1010, #5a0606);
}
.mag-page-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e8e8e8;
  color: #999;
  font-size: 14px;
}

.stf__parent {
  margin: 0 auto;
}
.stf__block {
  perspective: 2000px;
}

.mag-flipbook .stf__item.mag-cover-page.--simple {
  transform: translateX(-291px) !important;
}

/* ===== 左右翻页箭头 ===== */
.mag-nav-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 52px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  z-index: 30;
  transition: all 0.25s;
  border: 1px solid rgba(255, 255, 255, 0.06);
}
.mag-nav-arrow:hover {
  background: rgba(160, 0, 0, 0.6);
  color: #fff;
  border-color: rgba(160, 0, 0, 0.8);
  box-shadow: 0 4px 20px rgba(160, 0, 0, 0.3);
}
.mag-nav-arrow.disabled {
  opacity: 0.25;
  cursor: not-allowed;
}
.mag-nav-arrow.disabled:hover {
  background: rgba(0, 0, 0, 0.3);
  color: rgba(255, 255, 255, 0.6);
  border-color: rgba(255, 255, 255, 0.06);
  box-shadow: none;
}
.mag-nav-left { left: 24px; }
.mag-nav-right { right: 24px; }

/* ===== 底部缩略导航 ===== */
.mag-footer {
  height: 80px;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(12px);
  flex-shrink: 0;
  overflow-x: auto;
  overflow-y: hidden;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}
.mag-thumb-strip {
  display: flex;
  gap: 8px;
  padding: 10px 20px;
  height: 100%;
  align-items: center;
}
.mag-thumb {
  width: 60px;
  height: 80px;
  flex-shrink: 0;
  border: 2px solid transparent;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
  opacity: 0.5;
}
.mag-thumb:hover {
  border-color: rgba(255, 255, 255, 0.3);
  opacity: 0.85;
  transform: translateY(-2px);
}
.mag-thumb.active {
  border-color: #A00000;
  opacity: 1;
  box-shadow: 0 4px 12px rgba(160, 0, 0, 0.3);
}
.mag-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  background: #444;
}
.mag-thumb-num {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 10px;
  color: #fff;
  background: rgba(0, 0, 0, 0.65);
  line-height: 16px;
}
.mag-thumb-loading {
  cursor: default;
}
.mag-thumb-placeholder {
  width: 100%;
  height: 100%;
  background: #444;
}

/* ===== 购买弹窗 ===== */
.mag-buy-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.mag-buy-dialog {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  min-width: 500px;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  animation: mag-dialog-slide-up 0.3s ease-out;
}

@keyframes mag-dialog-slide-up {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.mag-dialog-header {
  padding: 24px 24px 16px;
  text-align: center;
  border-bottom: 1px solid #eee;
}

.mag-dialog-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: bold;
  color: #333;
  line-height: 1.4;
}

.mag-dialog-content {
  padding: 24px;
}

.mag-select-section {
  margin-bottom: 24px;
}

.mag-label {
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 12px;
  font-weight: 500;
}

.mag-radio-group {
  display: flex;
  gap: 24px;
}

.mag-radio-item {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.mag-radio-item input[type="radio"] {
  width: 18px;
  height: 18px;
  margin-right: 6px;
  accent-color: #A00000;
}

.mag-radio-text {
  font-size: 14px;
  color: #333;
}

.mag-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 12px 0;
}

.mag-info-label {
  font-size: 14px;
  color: #666;
}

.mag-info-value {
  font-size: 16px;
  color: #333;
  font-weight: 500;
}

.mag-quantity-section {
  margin-bottom: 20px;
}

.mag-quantity-control {
  display: flex;
  align-items: center;
  gap: 16px;
}

.mag-qty-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #ddd;
  background: #f5f5f5;
  border-radius: 6px;
  font-size: 18px;
  color: #333;
  cursor: pointer;
  transition: all 0.2s;
}

.mag-qty-btn:hover:not(:disabled) {
  background: #A00000;
  color: #fff;
  border-color: #A00000;
}

.mag-qty-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.mag-qty-value {
  font-size: 16px;
  color: #333;
  font-weight: 500;
  min-width: 40px;
  text-align: center;
}

.mag-total-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: #fafafa;
  border-radius: 8px;
  margin-bottom: 24px;
}

.mag-total-label {
  font-size: 16px;
  color: #666;
}

.mag-total-value {
  font-size: 24px;
  color: #A00000;
  font-weight: bold;
}

/* ===== 收货地址区域 ===== */
.mag-address-section {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.mag-input-row,
.mag-district-section {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

/* textarea 行顶部对齐 */
.mag-input-row.mag-input-row-textarea {
  align-items: flex-start;
}

/* 收货地址区域内的 label 固定宽度、右对齐 */
.mag-address-section .mag-label {
  display: inline-block;
  width: 100px;
  flex-shrink: 0;
  font-size: 14px;
  color: #333;
  font-weight: 400;
  margin-bottom: 0;
  line-height: 40px;
  text-align: right;
  padding-right: 12px;
  box-sizing: border-box;
}

/* textarea 那一行的 label 顶部对齐 */
.mag-input-row.mag-input-row-textarea .mag-label {
  line-height: 1.4;
  padding-top: 10px;
}

/* 输入框占据剩余空间 */
.mag-input-text,
.mag-input-textarea {
  flex: 1;
  width: auto;
  box-sizing: border-box;
}

.mag-input-text {
  padding: 0 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;
  height: 40px;
}

.mag-input-text:focus {
  outline: none;
  border-color: #A00000;
  box-shadow: 0 0 0 2px rgba(160, 0, 0, 0.1);
}

.mag-input-textarea {
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  resize: vertical;
  font-family: inherit;
  min-height: 80px;
}

.mag-input-textarea:focus {
  outline: none;
  border-color: #A00000;
  box-shadow: 0 0 0 2px rgba(160, 0, 0, 0.1);
}

/* 省市区选择行 */
.mag-district-section {
  flex-wrap: wrap;
}
.mag-district-section > .mag-label {
  line-height: 40px;
}
.mag-district-row {
  display: flex;
  gap: 12px;
  flex: 1;
  align-items: center;
  min-width: 0;
}

.mag-district-select {
  flex: 1;
  min-width: 0;
  padding: 0 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s;
  height: 40px;
  box-sizing: border-box;
}

.mag-district-select:hover:not(:disabled) {
  border-color: #A00000;
}

.mag-district-select:focus {
  outline: none;
  border-color: #A00000;
  box-shadow: 0 0 0 2px rgba(160, 0, 0, 0.1);
}

.mag-district-select:disabled {
  background: #f5f5f5;
  color: #999;
  cursor: not-allowed;
}

/* 警告文字 */
.mag-warning-text {
  flex-basis: 100%;
  margin-top: 12px;
  margin-left: 100px;
  padding: 10px 12px;
  background: #FFF3CD;
  border-left: 4px solid #FFCC00;
  color: #856404;
  font-size: 13px;
  border-radius: 4px;
  box-sizing: border-box;
}

.mag-pay-btn {
  width: 100%;
  height: 48px;
  background: linear-gradient(135deg, #A00000, #700000);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.25s;
  margin-top: 12px;
}

.mag-pay-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #C00000, #900000);
  box-shadow: 0 4px 20px rgba(160, 0, 0, 0.4);
}

/* 禁用态：变灰 */
.mag-pay-btn:disabled {
  background: #ccc;
  color: #fff;
  cursor: not-allowed;
  box-shadow: none;
  opacity: 0.7;
}

.mag-back-btn {
  width: 100%;
  height: 48px;
  background: transparent;
  color: #666;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 12px;
}

.mag-back-btn:hover {
  background: #f5f5f5;
  color: #333;
  border-color: #ccc;
}

.mag-buy-dialog-success {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  min-width: 420px;
  max-width: 500px;
  overflow: hidden;
  animation: mag-dialog-slide-up 0.3s ease-out;
}

.mag-buy-dialog-success .mag-dialog-content {
  padding: 48px 32px;
  text-align: center;
}

.mag-success-icon {
  margin-bottom: 24px;
}

.mag-success-title {
  margin: 0 0 16px;
  font-size: 24px;
  font-weight: bold;
  color: #333;
}

.mag-success-desc {
  margin: 0;
  font-size: 14px;
  color: #999;
  line-height: 1.8;
}

.mag-success-link {
  color: #A00000;
  cursor: pointer;
  font-weight: 500;
  text-decoration: underline;
}

.mag-success-link:hover {
  color: #C00000;
}

.mag-order-summary {
  margin-bottom: 24px;
  padding: 16px;
  background: #f9f9f9;
  border-radius: 8px;
  text-align: center;
}

.mag-order-summary p {
  margin: 0;
  font-size: 14px;
  color: #666;
  line-height: 1.6;
}

.mag-qrcode-container {
  text-align: center;
}

.mag-qrcode-box {
  width: 220px;
  height: 220px;
  margin: 0 auto 24px;
  border: 2px dashed #A00000;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
}

.mag-qrcode-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.mag-qrcode-placeholder span {
  font-size: 14px;
  color: #999;
}

.mag-pay-amount {
  font-size: 32px;
  color: #A00000;
  font-weight: bold;
}

.mag-dialog-fade-enter-active,
.mag-dialog-fade-leave-active {
  transition: opacity 0.3s ease;
}

.mag-dialog-fade-enter-from,
.mag-dialog-fade-leave-to {
  opacity: 0;
}
</style>